import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthUserStore } from '@qtek/libs/auth-user';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import { AccountOutput, PersonOutput, UserModel } from '@qtek/shared/models';
import { catchError, map, Observable, of, Subscription, take, tap } from 'rxjs';
import { MenuStore } from '../menu/menu.store';
import { PermissionsStore } from '../permission';
import { CompanyService } from './company.service';
import { PersonService } from './person.service';
import { UserStoreService } from './user.service';

interface UserAction {
  params: { sts: number; vndId: string };
}

export interface UserState {
  prs: PersonOutput;
  acn: AccountOutput | null;
  rel: any | null;
  actions: UserAction[] | null;
}

const initialState: UserState = {
  prs: null,
  acn: null,
  rel: null,
  actions: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      userService = inject(UserStoreService),
      personService = inject(PersonService),
      companyService = inject(CompanyService),
      permissionsStore = inject(PermissionsStore),
      menuStore = inject(MenuStore),
      snackBar = inject(SnackBarService),
      authUserStore = inject(AuthUserStore),
      router = inject(Router)
    ) => {
      const mergeBitMsks = (prev: any, next: any): any => {
        if (!next) return prev;
        return {
          ...prev,
          ...next,
          _prgs: (prev?._prgs ?? 0) | (next._prgs ?? 0),
        };
      };

      const mapToUserState = (res: any): UserState =>
        res == null
          ? initialState
          : {
              prs: res.prs ?? null,
              acn: res.acn ?? null,
              rel: res.rel ?? null,
              actions: res.actions ?? null,
            };

      const extractAcn = (res: any): any => (res as any)?.acn ?? res;

      let activeReloadSub: Subscription | null = null;

      const reloadUserAfterCompanyChange = (): void => {
        activeReloadSub?.unsubscribe();
        activeReloadSub = userService
          .getCurrentUser({ skipRevalidateErrorHandling: true })
          .pipe(
            take(1),
            tap(({ res: reloadedUser }) => {
              if (!reloadedUser) return;
              authUserStore.setUser(reloadedUser);
              if (reloadedUser.lic?.features) {
                permissionsStore.setPermissions(reloadedUser.lic.features);
              }
              patchState(store, mapToUserState(reloadedUser));
            }),
            catchError(() => of(null))
          )
          .subscribe();
      };

      const patchAuthUser = (
        override: Partial<Pick<UserModel, 'prs' | 'acn' | 'rel'>>
      ): void => {
        const currentUser = authUserStore.user();
        if (currentUser) {
          authUserStore.setUser({ ...currentUser, ...override });
        }
      };

      const mergeAndApplyPrs = (res: any): PersonOutput => {
        const newBitMsks = mergeBitMsks(store.prs()?.bitMsks, res.bitMsks);
        const newPrs = { ...store.prs(), ...res, bitMsks: newBitMsks };
        patchState(store, {
          prs: newPrs,
          actions: res.actions ?? store.actions(),
        });
        return newPrs;
      };

      const mergeAndApplyAcn = (res: any): AccountOutput | null => {
        const acn = extractAcn(res);
        if (!acn) return null;
        const newBitMsks = mergeBitMsks(store.acn()?.bitMsks, acn.bitMsks);
        const newAcn = { ...store.acn(), ...acn, bitMsks: newBitMsks };
        patchState(store, { acn: newAcn });
        return newAcn;
      };

      return {
        setUserData(data: any): void {
          patchState(store, mapToUserState(data));
        },

        getCurrentUser(payload: any = {}): Observable<boolean> {
          return userService.getCurrentUser(payload).pipe(
            tap(({ res }) => {
              authUserStore.setUser(res);
              if (res?.lic?.features) {
                permissionsStore.setPermissions(res.lic.features);
              } else {
                snackBar.showSnackBar({
                  type: 'ErrorMessage',
                  message: 'GNR_MISSED_PERMISSIONS',
                } as ErrorSnackBarData);
                router.navigate(['error']);
              }
              patchState(store, mapToUserState(res));
            }),
            map(() => true),
            catchError(() => of(false))
          );
        },

        updateCurrentUser(
          form: any,
          additionalPayload: any = {}
        ): Observable<boolean> {
          return personService.updatePerson(form, additionalPayload).pipe(
            tap(({ res }) => {
              if (!res) return;
              const newPrs = mergeAndApplyPrs(res);
              patchAuthUser({
                prs: newPrs,
                acn: store.acn(),
                rel: store.rel(),
              });
              snackBar.showSnackBar({
                type: 'NormalMessage',
                message: 'GNR_PRS_INFO_SAVED',
              } as NormalSnackBarData);
            }),
            map(() => true),
            catchError(() => of(false))
          );
        },

        updateCurrentUserCompany(
          payload: { form?: any; additionalPayload?: any },
          skipReload = false
        ): Observable<boolean> {
          return companyService
            .updateCompany(payload.additionalPayload, payload.form)
            .pipe(
              tap(({ res }) => {
                const newAcn = mergeAndApplyAcn(res);
                if (!newAcn) return;
                patchAuthUser({
                  prs: store.prs(),
                  acn: newAcn,
                  rel: store.rel(),
                });
                snackBar.showSnackBar({
                  type: 'NormalMessage',
                  message: 'GNR_CMP_INFO_SAVED',
                } as NormalSnackBarData);
                if (!skipReload) {
                  menuStore.loadMain();
                  menuStore.loadSetup();
                  reloadUserAfterCompanyChange();
                }
              }),
              map(() => true),
              catchError(() => of(false))
            );
        },

        applyPersonData(res: any): void {
          if (!res) return;
          const newPrs = mergeAndApplyPrs(res);
          patchAuthUser({ prs: newPrs, acn: store.acn(), rel: store.rel() });
        },

        applyCompanyData(res: any): void {
          const newAcn = mergeAndApplyAcn(res);
          if (!newAcn) return;
          patchAuthUser({ prs: store.prs(), acn: newAcn, rel: store.rel() });
        },
      };
    }
  ),
  withComputed(({ prs, acn, rel, actions }) => ({
    currentPerson: prs,
    mainCompany: acn,
    isAuthorized: computed(() => !!prs()?.prsId),
    personProgress: computed(() => prs()?.bitMsks?._prgs || 0),
    companyProgress: computed(() => acn()?.bitMsks?._prgs || 0),
    tutorialProgress: computed(() => prs()?.bitMsks?._uitut),
    currentPersonLanguage: computed(() => prs()?.lng),
    relationType: computed(() => rel()?.rl ?? null),
    userRelation: rel,
    isPersonalAccount: computed(
      () => !!(acn() && (acn()!.tp === 2000 || !acn()!.tp))
    ),
    userActions: actions,
    currentPersonTz: computed(() => prs()?.tz),
  }))
);
